/**
 * WHAT A LISTED ITEM COSTS — the grid's one answer, for its cell, its sort,
 * its filter and its export.
 *
 * A material or equipment costs its unit cost. A SERVICE does not: its cost is
 * its labor rows plus its parts and, for flat services, the row's `costBasis`
 * as Other cost. Dynamic apply-price writes its already-composed total into
 * that column, so dynamic services must not add it a second time. The grid
 * composes the same way the detail page does (`usePricebookItemEconomics`),
 * through the same `serviceCost` helper, from the parts' cost sources the list
 * carries.
 *
 * A dynamic service whose stored cost is positive is taken at its word, as on
 * the detail page — that is what `apply` wrote, alongside the stored price.
 */
import { serviceCardRevenue, serviceCost } from '@/lib/sales/service-cost';
import { flatServiceLaborLines } from '@/lib/sales/service-labor';
import type { LaborRateProfile, PricebookItem, ServiceLaborLine } from '@/types/pricebook';

export function pricebookListCost(
  item: PricebookItem,
  /**
   * The labor-rate profiles, or `null` while they are still loading. A
   * profile-backed labor row cannot be costed without them, and costing it at
   * zero in the meantime would sort, filter and export a too-low figure — so
   * such a service is UNKNOWN (a dash) until they arrive.
   */
  laborRateProfiles: LaborRateProfile[] | null,
): number | undefined {
  if (item.type !== 'service') return item.cost;

  const isDynamic = item.priceStrategy === 'DYNAMIC';
  if (isDynamic && item.cost != null && item.cost > 0) return item.cost;

  const lines: ServiceLaborLine[] = isDynamic
    ? (item.dynamicPricingConfig?.laborLines ?? [])
    : flatServiceLaborLines({
        laborCostLines: item.laborCostLines,
        estimatedHours: item.estimatedHours,
        laborCostRate: item.laborCostRate,
      });
  const parts = item.partCostSources ?? [];
  // Nothing to compose from: the stored figure is the only account there is.
  if (lines.length === 0 && parts.length === 0) return item.cost;
  if (laborRateProfiles === null && lines.some((line) => line.rateSource === 'PROFILE')) {
    return undefined;
  }

  // The card's revenue, so a margin-share row is costed at its share rather
  // than at $0 — the parts carry their billed price for exactly this.
  const composedCost = serviceCost(
    { laborLines: lines },
    parts,
    laborRateProfiles ?? [],
    serviceCardRevenue(item.price, parts),
  ).total;
  const otherCost = !isDynamic && Number.isFinite(item.cost) ? (item.cost ?? 0) : 0;
  return composedCost + otherCost;
}
